import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { JiraProject } from '@/types';
import { cn } from '@/utils/classname';

interface JiraProjectSearchProps {
  /** Currently selected project key */
  value?: string;
  /** Called when a project is selected */
  onChange: (projectKey: string) => void;
  /** Function to search for projects */
  searchProjects: (searchTerm: string) => Promise<JiraProject[]>;
  /** Initial projects to display (from empty search) */
  initialProjects: JiraProject[];
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Error state */
  error?: string;
  /** Input name for form */
  name?: string;
  /** Placeholder text */
  placeholder?: string;
}

export function JiraProjectSearch({
  value,
  onChange,
  searchProjects,
  initialProjects,
  disabled = false,
  error,
  name = 'projectKey',
  placeholder = 'Search for a project...',
}: JiraProjectSearchProps) {
  const [searchValue, setSearchValue] = useState('');
  const [projects, setProjects] = useState<JiraProject[]>(initialProjects);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedProject = projects.find(p => p.key === value);

  useEffect(() => {
    if (initialProjects.length > 0 && projects.length === 0) {
      setProjects(initialProjects);
    }
  }, [initialProjects, projects.length]);

  const debouncedSearch = useDebouncedCallback(async (term: string) => {
    setIsLoading(true);
    try {
      const results = await searchProjects(term);
      setProjects(results);
    } finally {
      setIsLoading(false);
    }
  }, 300);

  const handleSearchChange = useCallback(
    (newValue: string) => {
      setSearchValue(newValue);
      debouncedSearch(newValue);
    },
    [debouncedSearch]
  );

  const handleSelect = useCallback(
    (project: JiraProject | null) => {
      if (project) {
        onChange(project.key);
        setSearchValue('');
        setIsOpen(false);
      }
    },
    [onChange]
  );

  const displayValue = isOpen
    ? searchValue
    : selectedProject
      ? `${selectedProject.name} (${selectedProject.key})`
      : '';

  return (
    <Combobox value={selectedProject ?? null} onChange={handleSelect}>
      <div className="relative">
        <div className="relative">
          <MagnifyingGlassIcon className="text-chariot-text-secondary pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <ComboboxInput
            ref={inputRef}
            name={name}
            className={cn(
              'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary',
              'placeholder:text-chariot-text-tertiary w-full rounded-[2px] border py-1.5 pr-3 pl-9 text-sm',
              'focus:border-chariot-brand-primary focus:ring-chariot-brand-primary focus:ring-1 focus:outline-hidden',
              'disabled:cursor-not-allowed disabled:opacity-50',
              error &&
                'border-chariot-stroke-critical focus:border-chariot-stroke-critical focus:ring-chariot-stroke-critical'
            )}
            placeholder={placeholder}
            displayValue={() => displayValue}
            onChange={e => handleSearchChange(e.target.value)}
            onFocus={() => setIsOpen(true)}
            onBlur={() => {
              setTimeout(() => setIsOpen(false), 200);
            }}
            disabled={disabled}
            autoComplete="off"
          />
        </div>

        {isOpen && (
          <ComboboxOptions
            static
            className="border-chariot-stroke bg-chariot-background-primary absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-[2px] border-2 py-1 text-base shadow-lg"
          >
            {isLoading ? (
              <div className="text-chariot-text-secondary px-3 py-2 text-sm">
                Searching...
              </div>
            ) : projects.length === 0 ? (
              <div className="text-chariot-text-secondary px-3 py-2 text-sm">
                {searchValue
                  ? 'No projects found'
                  : 'Start typing to search projects'}
              </div>
            ) : (
              projects.map(project => (
                <ComboboxOption
                  key={project.key}
                  value={project}
                  className={({ focus }) =>
                    cn(
                      'text-chariot-text-primary relative cursor-default px-3 py-2 select-none',
                      focus && 'bg-chariot-background-secondary cursor-pointer'
                    )
                  }
                >
                  {({ selected }) => (
                    <span
                      className={cn(
                        'block truncate',
                        selected && 'font-semibold'
                      )}
                    >
                      {project.name} ({project.key})
                    </span>
                  )}
                </ComboboxOption>
              ))
            )}
          </ComboboxOptions>
        )}
      </div>
    </Combobox>
  );
}
